import { type ChangeEvent, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Link, Stack } from '@mui/material';
import { device_controller } from '../../../controllers/device_controller';
import { alphanumeric_comparator } from '../../../utils/helper_functions';
import { MHMTextField } from '../MHM/MHMTextField';
import { DeviceInfoSection } from './DeviceInfoSection';
import { DeviceLabels } from './DeviceLabels';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  selectedDeviceStateAtom,
  systemTypesSelector,
} from '../../../models/atoms/device_atoms';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { CheckPermission } from '../CheckPermission';
import { MHMLabel } from '../MHM/MHMLabel';
import { DisclaimerText, ErrorText } from '../MHM/MHMTypography';
import { capitalize } from 'lodash';

const ALERT_LABELS = [
  'Low battery',
  'No messages',
  'High ROC',
  'High Level',
  'blockage',
  'silt',
  'flood',
  'infiltration',
];

type Props = {
  canEditSession: boolean;
};

export function DeviceInfo({ canEditSession }: Props) {
  const { t } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const systemTypes = useRecoilValue(systemTypesSelector);
  const canManageDevices = useRecoilValue(canManageDevicesSelector);
  const [latitude, setLatitude] = useState('');
  const [latitudeError, setLatitudeError] = useState('');
  const [latitudeChanged, setLatitudeChanged] = useState(false);
  const [longitude, setLongitude] = useState('');
  const [longitudeError, setLongitudeError] = useState('');
  const [longitudeChanged, setLongitudeChanged] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentChanged, setCommentChanged] = useState(false);

  const canSaveLatitude = latitudeChanged && !latitudeError;
  const canSaveLongitude = longitudeChanged && !longitudeError;
  const canSaveLatitudeLongitude =
    canManageDevices && (canSaveLatitude || canSaveLongitude);

  function handleLatitudeChange(event: ChangeEvent<HTMLInputElement>) {
    if (!selectedDevice) {
      return;
    }

    setLatitude(event.target.value);
    const newLatitude = Number(event.target.value);
    if (isNaN(newLatitude) || event.target.value.trim() === '') {
      setLatitudeChanged(false);
      setLatitudeError('Please provide a valid number!');
      return;
    }
    if (newLatitude < -90 || newLatitude > 90) {
      setLatitudeChanged(false);
      setLatitudeError('Not in the allowable range degrees');
      return;
    }

    setLatitudeError('');
    setLatitudeChanged(true);
  }

  function handleLongitudeChange(event: ChangeEvent<HTMLInputElement>) {
    if (!selectedDevice) {
      return;
    }

    setLongitude(event.target.value);
    const newLongitude = Number(event.target.value);
    if (isNaN(newLongitude) || event.target.value.trim() === '') {
      setLongitudeChanged(false);
      setLongitudeError('Please provide a valid number!');
      return;
    }
    if (newLongitude < -180 || newLongitude > 180) {
      setLongitudeChanged(false);
      setLongitudeError('Not in the allowable range degrees');
      return;
    }

    setLongitudeError('');
    setLongitudeChanged(true);
  }

  async function saveLatitudeLongitude() {
    if (!selectedDevice || !canSaveLatitudeLongitude) {
      return;
    }

    await device_controller.changeDeviceLatitudeLongitude(
      selectedDevice.device_id,
      Number(latitude),
      Number(longitude),
    );

    setLatitudeChanged(false);
    setLongitudeChanged(false);
  }

  function handleCommentChange(event: ChangeEvent<HTMLInputElement>) {
    setCommentText(event.target.value);
    setCommentChanged(event.target.value !== selectedDevice?.comments);
  }

  function saveComments() {
    if (!selectedDevice) {
      return;
    }

    device_controller.change_device_comments(
      selectedDevice.device_id,
      commentText,
    );
    setCommentChanged(false);
  }

  useEffect(() => {
    if (!selectedDevice) {
      return;
    }

    setLatitude(String(selectedDevice.latitude));
    setLongitude(String(selectedDevice.longitude));
    setCommentText(selectedDevice.comments);
    setCommentChanged(false);
  }, [selectedDevice]);

  if (!selectedDevice) {
    return null;
  }

  const alertLabels = selectedDevice.labels
    .filter(label => ALERT_LABELS.includes(label))
    .sort(alphanumeric_comparator);

  return (
    <DeviceInfoSection>
      <DeviceInfoSection.Heading>{t('Device Info')}</DeviceInfoSection.Heading>

      <DeviceInfoSection.Entry label={t('Location:')}>
        <Link
          href={`https://www.google.com/maps/place/${selectedDevice.latitude},${selectedDevice.longitude}`}
          color="link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('Google Maps Link')}
        </Link>
      </DeviceInfoSection.Entry>

      <DeviceInfoSection.Entry label={t('Latitude:')}>
        <CheckPermission
          hasPermission={canManageDevices}
          sx={{
            maxWidth: '8rem',
            minWidth: '4rem',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0 0.25rem',
          }}
        >
          <MHMTextField
            size="small"
            value={latitude}
            disabled={!canEditSession}
            onChange={handleLatitudeChange}
            sx={{ flex: 1 }}
          />
          °
        </CheckPermission>
        {latitudeError ? (
          <ErrorText>{t(latitudeError, { min: '-90', max: '90' })}</ErrorText>
        ) : null}
      </DeviceInfoSection.Entry>

      <DeviceInfoSection.Entry label={t('Longitude:')}>
        <CheckPermission
          hasPermission={canManageDevices}
          sx={{
            maxWidth: '8rem',
            minWidth: '4rem',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0 0.25rem',
          }}
        >
          <MHMTextField
            size="small"
            value={longitude}
            disabled={!canEditSession}
            onChange={handleLongitudeChange}
            sx={{ flex: 1 }}
          />
          °
        </CheckPermission>
        {longitudeError ? (
          <ErrorText>
            {t(longitudeError, { min: '-180', max: '180' })}
          </ErrorText>
        ) : null}
      </DeviceInfoSection.Entry>

      {latitudeChanged || longitudeChanged ? (
        <DeviceInfoSection.Button
          sx={{ mb: 2 }}
          onClick={saveLatitudeLongitude}
          disabled={!canEditSession || !canSaveLatitudeLongitude}
        >
          {t('Save')}
        </DeviceInfoSection.Button>
      ) : null}

      <DeviceInfoSection.TextEntry
        label={t('Battery Percentage:')}
        text={`${selectedDevice.battery_percentage}%`}
      />

      <DeviceInfoSection.TextEntry
        label={t('Hardware Version:')}
        text={`ManholeMonitor ${capitalize(selectedDevice.sensor_type)} ${selectedDevice.hardware_version}`}
      />

      <DeviceInfoSection.TextEntry
        label={t('Firmware Version:')}
        text={selectedDevice.firmware_version}
      />

      {!!selectedDevice.mount_type && (
        <DeviceInfoSection.TextEntry
          label={t('Mounting Option:')}
          text={t(selectedDevice.mount_type)}
        />
      )}

      <DeviceInfoSection.Entry direction="column" label={t('Alerts:')}>
        {alertLabels.length === 0 ? null : (
          <Stack direction="row" gap={1} flexWrap="wrap" my={1}>
            {alertLabels.map(label => (
              <MHMLabel
                key={label}
                label={t(label)}
                labelProps={{ medium: true }}
                color="error"
                sx={{ flexBasis: 0, maxWidth: 'fit-content' }}
              />
            ))}
          </Stack>
        )}
        <DisclaimerText>
          {t('High level, Low battery, blockage alerts etc. will display here')}
        </DisclaimerText>
      </DeviceInfoSection.Entry>

      <DeviceInfoSection.Entry direction="column" label={t('Labels:')}>
        {systemTypes.includes(selectedDevice.system_type) ? (
          <MHMLabel
            label={t(selectedDevice.system_type)}
            labelProps={{ medium: true }}
            color="primary"
            sx={{ flexBasis: 0, maxWidth: 'fit-content' }}
          />
        ) : null}
        <DisclaimerText>
          {t('Navigate to settings page to create or delete labels')}
        </DisclaimerText>
        <CheckPermission
          hasPermission={canManageDevices}
          sx={{ width: '100%' }}
        >
          <DeviceLabels
            selectedDevice={selectedDevice}
            canEditSession={canEditSession}
          />
        </CheckPermission>
      </DeviceInfoSection.Entry>

      <DeviceInfoSection.Entry direction="column" label={t('Notes:')}>
        <MHMTextField
          multiline
          fullWidth // no effect, not sure why
          minRows={6}
          disabled={!canEditSession}
          value={commentText}
          onChange={handleCommentChange}
          slotProps={{
            root: { sx: { width: '100%' } },
            htmlInput: { 'data-testid': 'comments' },
          }}
          sx={{ width: '100%' }}
        />
        {commentChanged ? (
          <DeviceInfoSection.Button
            onClick={saveComments}
            data-testid={'save-comments'}
          >
            {t('Save')}
          </DeviceInfoSection.Button>
        ) : null}
      </DeviceInfoSection.Entry>
    </DeviceInfoSection>
  );
}
